import { useState } from "react";
import { api } from "../lib/api.js";
import { Table } from "./ui.js";
import { humanizeField } from "../lib/labels.js";

interface Answer {
  question: string;
  answer: string;
  sql: string;
  columns: string[];
  rows: Record<string, unknown>[];
  citation: string;
  suggestedChart: string;
}

const EXAMPLES = [
  "Faturamento de frete por região",
  "Quais motoristas com exame vencido?",
  "Metas por região na última competência",
  "Quais inconsistências existem nos dados?",
];

export function AgentChat({ compact = false }: { compact?: boolean }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<Answer[]>([]);
  const [err, setErr] = useState<string | null>(null);

  async function ask(question: string) {
    if (question.trim().length < 3) return;
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.post("/agent/ask", { question });
      setHistory((h) => [data, ...h]);
      setQ("");
    } catch (e) {
      setErr((e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Erro ao consultar o agente");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <input
          className="input"
          placeholder="Pergunte sobre operação, frete, metas, compliance…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && ask(q)}
        />
        <button className="btn-primary" onClick={() => ask(q)} disabled={loading}>
          {loading ? "…" : "Perguntar"}
        </button>
      </div>

      {!compact && history.length === 0 && (
        <div className="flex flex-wrap gap-2">
          {EXAMPLES.map((ex) => (
            <button key={ex} className="btn-ghost text-xs" onClick={() => ask(ex)}>{ex}</button>
          ))}
        </div>
      )}

      {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

      <div className={`space-y-3 ${compact ? "max-h-80 overflow-y-auto" : ""}`}>
        {history.map((a, i) => (
          <div key={i} className="card p-4">
            <div className="text-sm font-semibold text-slate-800">{a.question}</div>
            <div className="mt-1 text-sm text-slate-600">{a.answer}</div>
            {a.rows.length > 0 && (
              <div className="mt-3">
                <Table
                  columns={a.columns.slice(0, compact ? 3 : 8).map((c) => ({ key: c, label: humanizeField(c) }))}
                  rows={a.rows.slice(0, compact ? 5 : 50)}
                />
              </div>
            )}
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-slate-400">SQL · fonte: {a.citation}</summary>
              <pre className="mt-1 overflow-x-auto rounded bg-slate-900 p-2 text-xs text-slate-100">{a.sql}</pre>
            </details>
          </div>
        ))}
      </div>
    </div>
  );
}
